/**
 * The white chart card on the economic-outlook page — a fixed 550px slot
 * holding the GDP-view charts (nowcast + recession) or the inflation chart.
 *
 * GDP view mounts BOTH NowcastChart and RecessionChart at once, stacked in
 * the same 550px slot, and toggles them with `visibility` — not
 * `display`. This mirrors the legacy template
 * (`templates/pages/economic-outlook/index.html` lines 147-148), which
 * always keeps both `nowcasts-chart-*` and `recession-chart-*` divs in the
 * DOM (the inactive one hidden) and instantiates a Plotly chart in each on
 * load via `recession-probabilities.js`. So prod renders 2 Plotly charts
 * on `/economic-outlook` even on the default Nowcast view; React must too.
 *
 * `visibility:hidden` (rather than `display:none`) is deliberate: a Plotly
 * chart mounted inside a `display:none` box measures 0 height, and the
 * later flip to `display:block` fires no resize event react-plotly listens
 * for — so the chart stayed mis-sized and visibly jumped on first
 * interaction. A `visibility:hidden` box keeps its 550px layout box, so the
 * hidden chart measures the slot correctly on first paint and never jumps.
 *
 * Inflation view renders InflationChart alone — legacy puts inflation in a
 * separate `view-section`, so that view shows a single chart.
 */
import InflationChart from './InflationChart';
import NowcastChart from './NowcastChart';
import RecessionChart from './RecessionChart';
import { longName } from '../lib/countries';
import QueryState from '@/components/QueryState';
import type { ReactNode } from 'react';
import type {
  InflationDataResponse,
  InflationType,
  NowcastDataResponse,
  RecessionDataResponse,
} from '../types';
import type { UseQueryResult } from '@tanstack/react-query';

export interface OutlookChartCardProps {
  country: string;
  /** Which chart to show — derived from view + the GDP sub-selector. */
  active: 'nowcast' | 'recession' | 'inflation';
  inflationType: InflationType;
  nowcastQuery: UseQueryResult<NowcastDataResponse>;
  recessionQuery: UseQueryResult<RecessionDataResponse>;
  inflationQuery: UseQueryResult<InflationDataResponse>;
  onPointClick: (date: string, value: number | null) => void;
}

function errorLabel(kind: string) {
  return (error: unknown): string =>
    `Error loading ${kind} data: ${error instanceof Error ? error.message : 'unknown error'}`;
}

/**
 * One chart layer inside the 550px slot. The two GDP charts are absolutely
 * positioned so they occupy the same box; `visible` toggles `visibility`
 * (keeps layout, so Plotly always measures 550px). A `visibility:hidden`
 * element receives no pointer events and is skipped by tab focus per the
 * CSS spec, so the hidden layer never swallows clicks meant for the
 * visible chart.
 */
function ChartLayer({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="absolute inset-0 h-full"
      style={{ visibility: visible ? 'visible' : 'hidden' }}
      aria-hidden={!visible}
    >
      {children}
    </div>
  );
}

export default function OutlookChartCard({
  country,
  active,
  inflationType,
  nowcastQuery,
  recessionQuery,
  inflationQuery,
  onPointClick,
}: OutlookChartCardProps): JSX.Element {
  const isGdp = active === 'nowcast' || active === 'recession';

  return (
    <section
      className="chart-container-wrapper mb-8 overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      aria-label={`${longName(country)} ${active}`}
    >
      <div
        className="relative mx-auto w-full max-w-5xl overflow-hidden"
        style={{ height: 550, minHeight: 550, maxHeight: 550 }}
      >
        {isGdp ? (
          <>
            {/* Nowcast + Recession both mounted — mirrors the legacy DOM
                (two Plotly charts present at once, inactive one hidden). */}
            <ChartLayer visible={active === 'nowcast'}>
              <QueryState
                query={nowcastQuery}
                // The chart component handles its own empty state, so only
                // loading / error gate here.
                isEmpty={() => false}
                loadingLabel="Loading"
                errorLabel={errorLabel('nowcast')}
              >
                {(data) => (
                  <NowcastChart
                    country={country}
                    rows={data?.data ?? []}
                    onPointClick={onPointClick}
                  />
                )}
              </QueryState>
            </ChartLayer>
            <ChartLayer visible={active === 'recession'}>
              <QueryState
                query={recessionQuery}
                isEmpty={() => false}
                loadingLabel="Loading"
                errorLabel={errorLabel('recession')}
              >
                {(data) => <RecessionChart country={country} rows={data?.data ?? []} />}
              </QueryState>
            </ChartLayer>
          </>
        ) : (
          <ChartLayer visible>
            <QueryState
              query={inflationQuery}
              isEmpty={() => false}
              loadingLabel="Loading"
              errorLabel={errorLabel('inflation')}
            >
              {(data) => (
                <InflationChart
                  country={country}
                  inflationType={inflationType}
                  rows={data?.data ?? []}
                />
              )}
            </QueryState>
          </ChartLayer>
        )}
      </div>
    </section>
  );
}
